import { useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import {
  ChevronLeft,
  ChevronRight,
  Play,
  Clock,
  CheckCircle2,
  Download,
  FileText,
  MessageSquare,
  BookOpen,
} from 'lucide-react';
import { Course, Lecture, StudyMaterial } from '@/types/course';

interface CourseViewerProps {
  course: Course;
  onBack: () => void;
}

export default function CourseViewer({ course, onBack }: CourseViewerProps) {
  const [currentLectureIndex, setCurrentLectureIndex] = useState(0);
  const [activeTab, setActiveTab] = useState('lectures');

  // Mock lectures
  const lectures: Lecture[] = [
    {
      id: '1',
      courseId: course.id,
      title: 'Introduction to the Course',
      description: 'Get started with what you will learn in this course',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: 12,
      order: 1,
      completed: true,
      progress: 100,
      createdAt: new Date(),
    },
    {
      id: '2',
      courseId: course.id,
      title: 'Basic Concepts and Fundamentals',
      description: 'Learn the foundational concepts you need to know',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: 28,
      order: 2,
      completed: true,
      progress: 100,
      createdAt: new Date(),
    },
    {
      id: '3',
      courseId: course.id,
      title: 'Intermediate Topics',
      description: 'Dive deeper into more complex topics',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: 35,
      order: 3,
      completed: false,
      progress: 45,
      createdAt: new Date(),
    },
    {
      id: '4',
      courseId: course.id,
      title: 'Advanced Concepts',
      description: 'Master advanced topics and techniques',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: 42,
      order: 4,
      completed: false,
      progress: 0,
      createdAt: new Date(),
    },
    {
      id: '5',
      courseId: course.id,
      title: 'Practical Applications',
      description: 'Apply what you learned in real-world scenarios',
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      duration: 38,
      order: 5,
      completed: false,
      progress: 0,
      createdAt: new Date(),
    },
  ];

  // Mock study materials
  const studyMaterials: StudyMaterial[] = [
    {
      id: '1',
      courseId: course.id,
      title: 'Course Syllabus',
      type: 'pdf',
      url: '#',
      uploadedAt: new Date(),
    },
    {
      id: '2',
      courseId: course.id,
      title: 'Lecture Notes - Week 1',
      type: 'document',
      url: '#',
      uploadedAt: new Date(),
    },
    {
      id: '3',
      courseId: course.id,
      title: 'Practice Exercises',
      type: 'pdf',
      url: '#',
      uploadedAt: new Date(),
    },
    {
      id: '4',
      courseId: course.id,
      title: 'Reference Materials',
      type: 'document',
      url: '#',
      uploadedAt: new Date(),
    },
  ];

  const currentLecture = lectures[currentLectureIndex];
  const completedLectures = lectures.filter((l) => l.completed).length;
  const overallProgress = Math.round((completedLectures / lectures.length) * 100);

  const handleNextLecture = () => {
    if (currentLectureIndex < lectures.length - 1) {
      setCurrentLectureIndex((prev) => prev + 1);
    }
  };

  const handlePreviousLecture = () => {
    if (currentLectureIndex > 0) {
      setCurrentLectureIndex((prev) => prev - 1);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'pdf':
        return '📄';
      case 'document':
        return '📝';
      case 'image':
        return '🖼️';
      case 'link':
        return '🔗';
      default:
        return '📎';
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border sticky top-0 z-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={onBack} variant="ghost" size="sm">
                <ChevronLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">{course.name}</h1>
                <p className="text-sm text-muted-foreground">{course.subject}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Course Progress</p>
              <p className="text-2xl font-bold text-primary">{overallProgress}%</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Video Player Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2 space-y-6"
          >
            {/* Video Player */}
            <Card className="border-border overflow-hidden">
              <div className="relative w-full bg-black aspect-video">
                <iframe
                  src={currentLecture.videoUrl}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </div>
            </Card>

            {/* Lecture Info */}
            <div className="space-y-4">
              <div className="space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-primary">{currentLecture.title}</h2>
                    <p className="text-muted-foreground mt-1">{currentLecture.description}</p>
                  </div>
                  {currentLecture.completed && (
                    <Badge className="bg-green-100 text-green-800 gap-1">
                      <CheckCircle2 className="w-4 h-4" />
                      Completed
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {currentLecture.duration} minutes
                  </span>
                  <Separator orientation="vertical" className="h-4" />
                  <span>Lecture {currentLectureIndex + 1} of {lectures.length}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your Progress</span>
                  <span className="font-semibold text-primary">{currentLecture.progress}%</span>
                </div>
                <Progress value={currentLecture.progress} className="h-2" />
              </div>

              {/* Navigation Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handlePreviousLecture}
                  disabled={currentLectureIndex === 0}
                  variant="outline"
                  className="flex-1 gap-2 border-border"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </Button>
                <Button className="flex-1 bg-primary hover:bg-primary/90">
                  Mark as Complete
                </Button>
                <Button
                  onClick={handleNextLecture}
                  disabled={currentLectureIndex === lectures.length - 1}
                  className="flex-1 bg-secondary hover:bg-secondary/90 gap-2"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Tabs Section */}
            <Card className="border-border">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 border-b border-border rounded-none bg-muted/50">
                  <TabsTrigger value="lectures" className="gap-2">
                    <BookOpen className="w-4 h-4" />
                    <span className="hidden sm:inline">Details</span>
                  </TabsTrigger>
                  <TabsTrigger value="materials" className="gap-2">
                    <FileText className="w-4 h-4" />
                    <span className="hidden sm:inline">Materials</span>
                  </TabsTrigger>
                  <TabsTrigger value="discussion" className="gap-2">
                    <MessageSquare className="w-4 h-4" />
                    <span className="hidden sm:inline">Discussion</span>
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="lectures" className="p-6 space-y-4">
                  <h3 className="font-semibold text-foreground">Lecture Details</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    In this lecture, you will learn about {currentLecture.title.toLowerCase()}. We'll cover all the essential concepts and provide you with practical examples to help you understand the material better.
                  </p>
                  <div className="bg-primary/5 p-4 rounded-lg border border-primary/20">
                    <p className="text-sm font-medium text-primary mb-2">Key Topics:</p>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Fundamental concepts and definitions</li>
                      <li>• Practical applications and examples</li>
                      <li>• Common mistakes to avoid</li>
                      <li>• Practice exercises and assignments</li>
                    </ul>
                  </div>
                </TabsContent>

                <TabsContent value="materials" className="p-6 space-y-3">
                  <h3 className="font-semibold text-foreground mb-4">Study Materials</h3>
                  <div className="space-y-2">
                    {studyMaterials.map((material) => (
                      <div
                        key={material.id}
                        className="flex items-center justify-between p-3 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{getTypeIcon(material.type)}</span>
                          <div>
                            <p className="text-sm font-medium text-foreground">{material.title}</p>
                            <p className="text-xs text-muted-foreground">{material.type}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="gap-2">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="discussion" className="p-6 space-y-4">
                  <h3 className="font-semibold text-foreground mb-4">Discussion Forum</h3>
                  <div className="bg-muted/50 p-6 rounded-lg text-center space-y-3">
                    <MessageSquare className="w-12 h-12 text-muted-foreground mx-auto opacity-50" />
                    <p className="text-sm text-muted-foreground">
                      No discussions yet. Be the first to start a conversation!
                    </p>
                    <Button className="bg-primary hover:bg-primary/90 gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Start Discussion
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </Card>
          </motion.div>

          {/* Sidebar - Lecture List */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Card className="border-border sticky top-24 max-h-[calc(100vh-120px)]">
              <div className="p-6 border-b border-border">
                <h3 className="font-semibold text-foreground">
                  Lectures ({completedLectures}/{lectures.length})
                </h3>
                <Progress value={overallProgress} className="mt-3 h-2" />
              </div>

              <ScrollArea className="h-full">
                <div className="p-4 space-y-2">
                  {lectures.map((lecture, index) => (
                    <motion.button
                      key={lecture.id}
                      onClick={() => setCurrentLectureIndex(index)}
                      whileHover={{ x: 4 }}
                      className={`w-full text-left p-3 rounded-lg border transition-all duration-200 group ${
                        currentLectureIndex === index
                          ? 'bg-primary/10 border-primary'
                          : 'border-border hover:border-secondary hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="mt-1">
                          {lecture.completed ? (
                            <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
                          ) : (
                            <Play className="w-5 h-5 text-muted-foreground group-hover:text-primary flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {index + 1}. {lecture.title}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {lecture.duration} min
                          </p>
                        </div>
                      </div>
                    </motion.button>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
